import React, { useState, useEffect, useCallback } from 'react';
import { Truck, MapPin, Phone, Clock, User, CheckCircle, XCircle, RefreshCw, Navigation } from 'lucide-react';
import { API_ENDPOINTS } from '../config/apiRouting';
import { sendDeliveryNotification } from '../services/orderNotifications';

interface DeliveryManagementProps {
  user: any;
  storeId?: string;
}

interface Driver {
  driverId: string;
  name: string;
  phone: string;
  vehicleInfo: string;
  status: 'available' | 'on_delivery' | 'offline';
  currentOrderId?: string;
  currentLocation?: {
    lat: number;
    lng: number;
    updatedAt: string;
  };
}

interface DeliveryOrder {
  orderId: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  deliveryAddress: string;
  items: Array<{ name: string; quantity: number }>;
  total: number;
  status: 'ready' | 'assigned' | 'out_for_delivery' | 'delivered';
  assignedDriver?: Driver;
  createdAt: string;
  estimatedDeliveryTime?: string;
}

const DeliveryManagement: React.FC<DeliveryManagementProps> = ({ user, storeId }) => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [orders, setOrders] = useState<DeliveryOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [assigningDriver, setAssigningDriver] = useState<string | null>(null);

  // Mock initial data for demo
  useEffect(() => {
    // Demo drivers
    setDrivers([
      {
        driverId: 'driver-1',
        name: 'Alex Thompson',
        phone: '(555) 123-4567',
        vehicleInfo: 'Honda Civic - Silver',
        status: 'available'
      },
      {
        driverId: 'driver-2',
        name: 'Jordan Martinez',
        phone: '(555) 234-5678',
        vehicleInfo: 'Toyota Camry - Blue',
        status: 'available'
      },
      {
        driverId: 'driver-3',
        name: 'Sam Wilson',
        phone: '(555) 345-6789',
        vehicleInfo: 'Ford Focus - White',
        status: 'on_delivery',
        currentOrderId: 'demo-order-1'
      }
    ]);

    // Demo delivery orders
    setOrders([
      {
        orderId: 'demo-order-1',
        orderNumber: 'DEL-001',
        customerName: 'John D.',
        customerPhone: '(555) 987-6543',
        deliveryAddress: '123 Main St, Apt 4B',
        items: [
          { name: 'Grilled Salmon', quantity: 1 },
          { name: 'Caesar Salad', quantity: 1 }
        ],
        total: 34.99,
        status: 'out_for_delivery',
        assignedDriver: {
          driverId: 'driver-3',
          name: 'Sam Wilson',
          phone: '(555) 345-6789',
          vehicleInfo: 'Ford Focus - White',
          status: 'on_delivery'
        },
        createdAt: new Date(Date.now() - 25 * 60000).toISOString(),
        estimatedDeliveryTime: new Date(Date.now() + 10 * 60000).toISOString()
      },
      {
        orderId: 'demo-order-2',
        orderNumber: 'DEL-002',
        customerName: 'Maria S.',
        customerPhone: '(555) 876-5432',
        deliveryAddress: '456 Oak Ave, Suite 200',
        items: [
          { name: 'Beef Tenderloin', quantity: 2 },
          { name: 'Red Wine', quantity: 1 }
        ],
        total: 89.50,
        status: 'ready',
        createdAt: new Date(Date.now() - 5 * 60000).toISOString()
      }
    ]);
  }, []);

  // Fetch real orders from API
  const fetchDeliveryOrders = useCallback(async () => {
    if (!storeId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.posSyncOrders()}?storeId=${storeId}&orderType=delivery&status=ready,assigned,out_for_delivery`);
      if (response.ok) {
        const data = await response.json();
        const apiOrders = data.orders || data.data || [];

        // Transform to delivery order format
        const deliveryOrders: DeliveryOrder[] = apiOrders.map((order: any) => ({
          orderId: order.checkId || order.orderId || order.id,
          orderNumber: order.orderNumber || `DEL-${(order.checkId || order.id || '').slice(-3)}`,
          customerName: order.customerInfo?.name || 'Customer',
          customerPhone: order.customerInfo?.phone || '',
          deliveryAddress: [order.customerInfo?.address, order.customerInfo?.city, order.customerInfo?.zipCode].filter(Boolean).join(', '),
          items: (order.items || []).map((item: any) => ({
            name: item.name || item.menuItemName,
            quantity: item.quantity || 1
          })),
          total: order.total || 0,
          status: order.status === 'out_for_delivery' ? 'out_for_delivery' : order.driver ? 'assigned' : 'ready',
          assignedDriver: order.driver,
          createdAt: order.createdAt || new Date().toISOString(),
          estimatedDeliveryTime: order.estimatedDeliveryTime
        }));

        // Merge with existing demo orders
        setOrders(prev => {
          const demoOrders = prev.filter(o => o.orderId.startsWith('demo-'));
          return [...deliveryOrders, ...demoOrders];
        });
      }
    } catch (error) {
      console.error('Failed to fetch delivery orders:', error);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchDeliveryOrders();
    // Poll every 30 seconds
    const interval = setInterval(fetchDeliveryOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchDeliveryOrders]);

  const assignDriver = async (orderId: string, driverId: string) => {
    setAssigningDriver(orderId);

    try {
      const driver = drivers.find(d => d.driverId === driverId);
      const order = orders.find(o => o.orderId === orderId);

      if (!driver || !order) return;

      // Update order status via API (for non-demo orders)
      if (!orderId.startsWith('demo-')) {
        try {
          await fetch(`${API_ENDPOINTS.posSyncOrders()}/${orderId}/assign-driver`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              driverId: driver.driverId,
              driverName: driver.name,
              driverPhone: driver.phone,
              vehicleInfo: driver.vehicleInfo
            })
          });
        } catch (apiError) {
          console.error('Failed to assign driver via API:', apiError);
        }
      }

      // Update local state
      setOrders(prev => prev.map(o =>
        o.orderId === orderId
          ? { ...o, status: 'assigned', assignedDriver: { ...driver, status: 'on_delivery' } }
          : o
      ));

      setDrivers(prev => prev.map(d =>
        d.driverId === driverId
          ? { ...d, status: 'on_delivery', currentOrderId: orderId }
          : d
      ));

      setSelectedOrder(null);
    } catch (error) {
      console.error('Failed to assign driver:', error);
    } finally {
      setAssigningDriver(null);
    }
  };

  const markOutForDelivery = async (orderId: string) => {
    const order = orders.find(o => o.orderId === orderId);
    if (!order) return;

    try {
      // Update order status via API
      if (!orderId.startsWith('demo-')) {
        await fetch(`${API_ENDPOINTS.posSyncOrders()}/${orderId}/status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: 'out_for_delivery',
            timestamp: new Date().toISOString()
          })
        });
      }

      // Send notification to customer
      if (order.customerPhone) {
        sendDeliveryNotification({
          orderId: order.orderId,
          storeId: storeId || '',
          storeName: 'Restaurant',
          orderType: 'delivery',
          customerInfo: {
            name: order.customerName,
            phone: order.customerPhone
          },
          items: order.items.map(i => ({ ...i, price: 0 })),
          subtotal: order.total,
          tax: 0,
          tip: 0,
          total: order.total,
          status: 'out_for_delivery'
        }, 'out_for_delivery');
      }

      setOrders(prev => prev.map(o =>
        o.orderId === orderId
          ? {
              ...o,
              status: 'out_for_delivery',
              estimatedDeliveryTime: new Date(Date.now() + 20 * 60000).toISOString()
            }
          : o
      ));
    } catch (error) {
      console.error('Failed to update delivery status:', error);
    }
  };

  const markDelivered = async (orderId: string) => {
    const order = orders.find(o => o.orderId === orderId);
    if (!order) return;

    try {
      // Update order status via API
      if (!orderId.startsWith('demo-')) {
        await fetch(`${API_ENDPOINTS.posSyncOrders()}/${orderId}/status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: 'delivered',
            timestamp: new Date().toISOString()
          })
        });
      }

      // Free up the driver
      if (order.assignedDriver) {
        setDrivers(prev => prev.map(d =>
          d.driverId === order.assignedDriver?.driverId
            ? { ...d, status: 'available', currentOrderId: undefined }
            : d
        ));
      }

      // Remove from active orders
      setOrders(prev => prev.filter(o => o.orderId !== orderId));
    } catch (error) {
      console.error('Failed to mark as delivered:', error);
    }
  };

  const getTimeAgo = (isoString: string) => {
    const minutes = Math.floor((Date.now() - new Date(isoString).getTime()) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes === 1) return '1 min ago';
    return `${minutes} min ago`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'assigned': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'out_for_delivery': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'delivered': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const availableDrivers = drivers.filter(d => d.status === 'available');
  const pendingOrders = orders.filter(o => o.status === 'ready');
  const activeDeliveries = orders.filter(o => o.status === 'assigned' || o.status === 'out_for_delivery');

  return (
    <div className="modern-card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Truck className="w-6 h-6 text-blue-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">Delivery Management</h2>
        </div>
        <button
          onClick={fetchDeliveryOrders}
          disabled={loading}
          className="btn-modern-secondary flex items-center"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <p className="text-gray-600 mb-6">
        Assign drivers to delivery orders and track their progress in real-time.
      </p>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-50 rounded-lg p-4 text-center border border-yellow-200">
          <div className="text-2xl font-bold text-yellow-700">{pendingOrders.length}</div>
          <div className="text-sm text-yellow-600">Awaiting Assignment</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
          <div className="text-2xl font-bold text-blue-700">{activeDeliveries.length}</div>
          <div className="text-sm text-blue-600">Active Deliveries</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
          <div className="text-2xl font-bold text-green-700">{availableDrivers.length}</div>
          <div className="text-sm text-green-600">Available Drivers</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Orders */}
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-yellow-600" />
            Ready for Delivery
          </h3>
          {pendingOrders.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500">
              No orders waiting for delivery
            </div>
          ) : (
            <div className="space-y-4">
              {pendingOrders.map(order => (
                <div key={order.orderId} className={`border-2 rounded-lg p-4 ${getStatusColor(order.status)}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-semibold text-lg">#{order.orderNumber}</div>
                      <div className="text-sm opacity-75">{order.customerName}</div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="opacity-75">{getTimeAgo(order.createdAt)}</div>
                      <div className="font-semibold">${order.total.toFixed(2)}</div>
                    </div>
                  </div>

                  <div className="flex items-start mb-3">
                    <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{order.deliveryAddress}</span>
                  </div>

                  <div className="text-sm mb-3">
                    {order.items.map((item, i) => (
                      <span key={i}>
                        {i > 0 ? ', ' : ''}{item.quantity}x {item.name}
                      </span>
                    ))}
                  </div>

                  {selectedOrder === order.orderId ? (
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Select Driver:</div>
                      {availableDrivers.length === 0 ? (
                        <div className="text-sm text-red-600">No drivers available</div>
                      ) : (
                        availableDrivers.map(driver => (
                          <button
                            key={driver.driverId}
                            onClick={() => assignDriver(order.orderId, driver.driverId)}
                            disabled={assigningDriver === order.orderId}
                            className="w-full flex items-center justify-between p-2 bg-white rounded border hover:border-blue-500 transition-colors"
                          >
                            <div className="flex items-center">
                              <User className="w-4 h-4 mr-2" />
                              <span className="font-medium">{driver.name}</span>
                            </div>
                            <span className="text-xs text-gray-500">{driver.vehicleInfo}</span>
                          </button>
                        ))
                      )}
                      <button
                        onClick={() => setSelectedOrder(null)}
                        className="w-full p-2 text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setSelectedOrder(order.orderId)}
                      className="w-full flex items-center justify-center p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Assign Driver
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active Deliveries */}
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Navigation className="w-5 h-5 mr-2 text-purple-600" />
            Active Deliveries
          </h3>
          {activeDeliveries.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500">
              No active deliveries
            </div>
          ) : (
            <div className="space-y-4">
              {activeDeliveries.map(order => (
                <div key={order.orderId} className={`border-2 rounded-lg p-4 ${getStatusColor(order.status)}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-semibold text-lg">#{order.orderNumber}</div>
                      <div className="text-sm opacity-75">{order.customerName}</div>
                    </div>
                    <div className="text-xs px-2 py-1 rounded-full bg-white">
                      {order.status === 'assigned' ? 'Assigned' : 'On the Way'}
                    </div>
                  </div>

                  <div className="flex items-start mb-3">
                    <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{order.deliveryAddress}</span>
                  </div>

                  {order.assignedDriver && (
                    <div className="bg-white bg-opacity-50 rounded p-2 mb-3">
                      <div className="flex items-center text-sm">
                        <User className="w-4 h-4 mr-2" />
                        <span className="font-medium">{order.assignedDriver.name}</span>
                      </div>
                      <div className="flex items-center text-xs mt-1 opacity-75">
                        <Phone className="w-3 h-3 mr-1" />
                        {order.assignedDriver.phone}
                        <span className="mx-2">|</span>
                        {order.assignedDriver.vehicleInfo}
                      </div>
                    </div>
                  )}

                  {order.estimatedDeliveryTime && (
                    <div className="text-sm mb-3 flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      Est. arrival: {new Date(order.estimatedDeliveryTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  )}

                  <div className="flex gap-2">
                    {order.status === 'assigned' && (
                      <button
                        onClick={() => markOutForDelivery(order.orderId)}
                        className="flex-1 flex items-center justify-center p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                      >
                        <Truck className="w-4 h-4 mr-2" />
                        Mark Picked Up
                      </button>
                    )}
                    <button
                      onClick={() => markDelivered(order.orderId)}
                      className="flex-1 flex items-center justify-center p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark Delivered
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Drivers List */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <User className="w-5 h-5 mr-2 text-gray-600" />
          Drivers
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {drivers.map(driver => (
            <div
              key={driver.driverId}
              className={`border rounded-lg p-4 ${
                driver.status === 'available'
                  ? 'border-green-300 bg-green-50'
                  : driver.status === 'on_delivery'
                  ? 'border-blue-300 bg-blue-50'
                  : 'border-gray-300 bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium">{driver.name}</div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  driver.status === 'available'
                    ? 'bg-green-200 text-green-800'
                    : driver.status === 'on_delivery'
                    ? 'bg-blue-200 text-blue-800'
                    : 'bg-gray-200 text-gray-800'
                }`}>
                  {driver.status === 'available' ? 'Available' : driver.status === 'on_delivery' ? 'On Delivery' : 'Offline'}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <div className="flex items-center">
                  <Phone className="w-3 h-3 mr-2" />
                  {driver.phone}
                </div>
                <div className="flex items-center mt-1">
                  <Truck className="w-3 h-3 mr-2" />
                  {driver.vehicleInfo}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeliveryManagement;
